// src/components/AGTFit/workout/exercisesVisual/ExerciseViewDialog.js
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { X, Dumbbell, Pause, Play } from 'lucide-react';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilo para diálogos estilo Apple
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    [theme.breakpoints.up('sm')]: {
      borderRadius: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0px',
      margin: 0,
      maxHeight: '100%',
      height: '100%',
    },
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    overflow: 'hidden',
    zIndex: theme.zIndex.modal + 5, // Z-index aumentado para estar por encima
  },
  // Z-index del componente principal también aumentado
  zIndex: theme.zIndex.modal + 5
}));

// Contenedor de media mejorado para ajustarse perfectamente
const MediaContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 250,
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bgLight : '#FFFFFF',
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

// Botón estilo Apple para video
const MediaControlButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 32,
  height: 32,
  backgroundColor: 'rgba(128, 128, 128, 0.6)',
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  borderRadius: '50%',
  padding: 6,
  '&:hover': {
    backgroundColor: 'rgba(128, 128, 128, 0.8)',
  },
  zIndex: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  transition: 'all 0.2s ease-in-out',
}));

// Contenedor para los datos del ejercicio
const ExerciseInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  marginBottom: theme.spacing(2)
}));

const ExerciseViewDialog = ({ open, onClose, exercise }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef(null);
  
  // Función para alternar reproducción/pausa
  const toggleVideoPlayback = (e) => {
    e.stopPropagation();
    
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };
  
  // Renderizar media (imagen o video) con ajuste perfecto y loop para videos
  const renderMedia = () => {
    if (!exercise) return null;
    
    if (exercise.videoUrl) {
      return (
        <MediaContainer>
          <video 
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain'
            }}
          >
            <source src={exercise.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          <MediaControlButton onClick={toggleVideoPlayback}>
            {isVideoPlaying ? 
              <Pause size={16} color="#FFFFFF" /> : 
              <Play size={16} color="#FFFFFF" />
            }
          </MediaControlButton>
        </MediaContainer>
      );
    } else if (exercise.imageUrl) {
      return (
        <MediaContainer>
          <img 
            src={exercise.imageUrl} 
            alt={exercise.name}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain'
            }}
          />
        </MediaContainer>
      );
    } else {
      return (
        <MediaContainer>
          <Dumbbell size={60} color={FIT_ORANGE} opacity={0.3} />
        </MediaContainer>
      );
    }
  };
  
  if (!exercise) return null;
  
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
      }}>
        <Typography variant="h5" fontWeight={600}>
          {exercise.name}
        </Typography>
        
        <IconButton 
          onClick={onClose} 
          sx={{ 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
            }
          }}
        >
          <X size={20} />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Mostrar imagen o video */}
        {renderMedia()}
        
        <ExerciseInfoBox>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Details
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {exercise.primaryMuscle && exercise.primaryMuscle.charAt(0).toUpperCase() + exercise.primaryMuscle.slice(1).toLowerCase()} 
            {exercise.equipment && ` • ${exercise.equipment.charAt(0).toUpperCase() + exercise.equipment.slice(1).toLowerCase()}`}
            {!exercise.equipment && ' • No Equipment'}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Instructions
          </Typography>
          
          {exercise.description ? (
            <Typography variant="body2" color="text.secondary">
              {exercise.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No instructions available for this exercise.
            </Typography>
          )}
        </ExerciseInfoBox>
      </DialogContent>
    </StyledDialog>
  );
};

export default ExerciseViewDialog;